import { css } from "@emotion/css";
import { tableBorder } from "@octopusdeploy/design-system-components";
import { StringHelper, type DeprecatedSelectOptions } from "@octopusdeploy/design-system-octopus-components";
import { borderWidth, fontWeight, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { ActionTemplateParameterResource, ChannelResource, EnvironmentV2Resource, FeedResource, GetTeamsLookupBffResponseTeam, PackageReference, PlatformHubAccountSummary, ProjectSummaryResource, PropertyValueResource, ScopeSpecification, ScopeValues, TemplateParameterResource, VariableType, WorkerPoolResource, } from "@octopusdeploy/octopus-server-client";
import { ControlType, isAccountControlType } from "@octopusdeploy/octopus-server-client";
import { noOp } from "@octopusdeploy/utilities";
import type * as React from "react";
import type { ParameterValueRow } from "~/areas/platformHub/processTemplates/TemplateParametersTable";
import { isFailedPermissionCheck } from "~/areas/projects/components/Process/ProcessStepsLayoutTypes";
import type { LoadedLibraryVariableSets } from "~/areas/projects/components/Variables/AllVariables/SharedAllVariablesPage";
import { getVariableTypeFromDisplaySettings } from "~/areas/projects/components/Variables/TenantVariables/getVariableTypeFromDisplaySettings";
import MissingVariablesIcon from "~/areas/tenants/MissingVariablesIcon/MissingVariablesIcon";
import { isAccountType } from "~/areas/variables/isAccountType";
import type { ProjectSourceItems, ProjectTemplateSourceItems } from "~/components/ActionTemplateParameterInput/ActionTemplateParameterInput";
import { createSummaryFor } from "~/components/ActionTemplateParameterInput/ActionTemplateParameterInputExpandableFormElement";
import convertPropertyValueResourceToString from "~/components/convertPropertyValueResourceToString";
import { Summary, Text } from "~/components/form/index";
import { JsonUtils } from "~/utils/jsonUtils";
export interface ProjectSourceVariable {
    Name: string;
    Scope: ScopeSpecification;
    Type: VariableType;
}
export const defaultScopeValues: ScopeValues = {
    Actions: [],
    Channels: [],
    Environments: [],
    Machines: [],
    Roles: [],
    TenantTags: [],
    Processes: [],
};
export const getAllVariables = (sourceItems: ProjectSourceItems | ProjectTemplateSourceItems): ProjectSourceVariable[] => {
    const processVariables = isFailedPermissionCheck(sourceItems.variableSet) ? [] : sourceItems.variableSet.Variables;
    const libraryVariables = isFailedPermissionCheck(sourceItems.libraryVariableSets) ? [] : sourceItems.libraryVariableSets.flatMap((librarySet: LoadedLibraryVariableSets) => librarySet.variableSet.Variables);
    // The "Scope" property determines which parameter default value environment scopes appear as overridden/crossed out.
    // Tenant variable templates don't have scopes - only their values do, so we default to no scopes.
    // This causes all parameter default value environment scopes to appear crossed out,
    // even when a tenant variable may not actually apply in a particular environment.
    const tenantVariables = sourceItems.actionTemplateParameters.map((at: ActionTemplateParameterResource): ProjectSourceVariable => ({
        Name: at.Name,
        Type: getVariableTypeFromDisplaySettings(at.DisplaySettings["Octopus.ControlType"]),
        Scope: {},
    }));
    return [...processVariables, ...libraryVariables, ...tenantVariables];
};
export const getUniqueAccountVariableNames = (sourceItems: ProjectSourceItems | ProjectTemplateSourceItems): string[] => [
    ...new Set(getAllVariables(sourceItems)
        .filter((v) => isAccountType(v.Type))
        .map((v) => v.Name)),
];
export const isOverrideVariableRow = (row: ParameterValueRow, platformHubAccounts: PlatformHubAccountSummary[]): boolean => {
    if (!row.Value) {
        return false;
    }
    // If it has a value, and it's not a platform hub account then it must be overridden with a variable.
    return !platformHubAccounts.find((a) => a.Id === row.Value?.Value);
};
export const createSummaryForParameterValue = (label: string, type: ControlType, options: DeprecatedSelectOptions, feeds: FeedResource[], environments: EnvironmentV2Resource[], workerPools: WorkerPoolResource[], teams: GetTeamsLookupBffResponseTeam[], channels: ChannelResource[], projects: ProjectSummaryResource[], packageParameterIsUsed?: boolean) => {
    // Override the createSummaryFor() function to handle package parameters a specific way
    return (value: PropertyValueResource | undefined) => {
        if (typeof value === "string" && type === "Package" && JsonUtils.tryParseJson(value)) {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            const pkg = JSON.parse(value) as Partial<PackageReference<unknown>>;
            if (!pkg.PackageId) {
                return Summary.placeholder(<div className={templateParameterValueStyles.noValue}>
                        <MissingVariablesIcon show={true}/>
                        <Text value={""} placeholder={"Provide value"} onChange={noOp} hideUnderline={true} monoSpacedFont={true}/>
                    </div>);
            }
        }
        return createSummaryFor(label, type, options, feeds, environments, workerPools, teams, channels, projects, packageParameterIsUsed)(value);
    };
};
export const getAccounts = (platformHubAccounts: PlatformHubAccountSummary[], sourceItems: ProjectSourceItems | ProjectTemplateSourceItems) => {
    const platformHubAccountIdToNameObject = Object.fromEntries(platformHubAccounts.map((a) => [a.Id, a.Name]));
    const uniqueAccountVariableNames = getUniqueAccountVariableNames(sourceItems);
    const accountVariableNameToFormattedNameObject = Object.fromEntries(uniqueAccountVariableNames.map((name) => [name, name]));
    return { ...platformHubAccountIdToNameObject, ...accountVariableNameToFormattedNameObject };
};
export const getControlTypeFromParameterValueRow = (row: ParameterValueRow): ControlType => row.Parameter.DisplaySettings["Octopus.ControlType"] ?? ControlType.SingleLineText;
export const isAccountControlTypeWithoutDefaultValues = (data: ParameterValueRow): boolean => {
    const controlType = data.Parameter.DisplaySettings["Octopus.ControlType"] ?? ControlType.SingleLineText;
    return isAccountControlType(controlType) && !data.Value;
};
export const renderSummaryNodeForParameterValue = (node: React.ReactNode) => {
    if (typeof node === "string") {
        return <Text value={node.toString()} placeholder={node.toString()} onChange={noOp} hideUnderline={true} showValueAsTitleAttribute={true} monoSpacedFont={true}/>;
    }
    return <div className={templateParameterValueStyles.summaryNode}>{node}</div>;
};
export const partitionParameterValueRowsByAccountType = (rows: ParameterValueRow[]): ParameterValueRow[] => {
    const accountRows: ParameterValueRow[] = [];
    const nonAccountRows: ParameterValueRow[] = [];
    for (const row of rows) {
        if (isAccountControlType(getControlTypeFromParameterValueRow(row))) {
            accountRows.push(row);
        }
        else {
            nonAccountRows.push(row);
        }
    }
    return [...accountRows, ...nonAccountRows];
};
export const shouldRenderParameter = (parameter: TemplateParameterResource, value: PropertyValueResource | undefined, availableWorkerPools: WorkerPoolResource[], isTenanted: boolean, isProjectTemplate: boolean = false) => {
    const controlType = parameter.DisplaySettings["Octopus.ControlType"];
    if (controlType === ControlType.WorkerPool) {
        // For project templates, we always show WorkerPool parameters because they can reference
        // project template parameters even when there are no actual worker pools available
        return availableWorkerPools.length > 0 || isProjectTemplate;
    }
    if (controlType === ControlType.TenantTags) {
        const stringValue = convertPropertyValueResourceToString(value);
        if (!isTenanted && StringHelper.isNullOrWhiteSpace(stringValue)) {
            return false;
        }
    }
    return true;
};
export const baseCellHeightInRem = 3.75;
const multipleValuesAndScopesIndicatorHeightInRem = 1.75;
export const templateParameterValueStyles = {
    calloutContainer: css({
        display: "flex",
        flexDirection: "column",
        gap: space[8],
    }),
    removedParameter: css({
        color: themeTokens.color.text.disabled,
    }),
    parameterNameCellPrimaryHeader: css({
        font: text.body.regular.small,
        fontWeight: fontWeight["700"],
        overflowWrap: "anywhere",
        hyphens: "auto",
    }),
    parameterNameCellSecondaryHeader: css({
        font: text.code.regular.small,
        color: themeTokens.color.text.tertiary,
        overflowWrap: "anywhere",
        hyphens: "auto",
    }),
    parameterHelpText: css({
        font: text.body.regular.small,
        overflowWrap: "anywhere",
        hyphens: "auto",
    }),
    valueContainer: css({
        paddingLeft: space[12],
        paddingRight: space[12],
        width: "100%",
        minHeight: `${baseCellHeightInRem}rem`,
        display: "flex",
        alignItems: "center",
    }),
    disabledText: css({
        color: themeTokens.color.text.disabled,
    }),
    popoverContainer: css({
        paddingLeft: space[12],
        paddingRight: space[12],
        paddingBottom: space[4],
        width: "100%",
    }),
    noValue: css({
        display: "flex",
        alignItems: "center",
        width: "100%",
        gap: space[8],
    }),
    scopeContainer: css({
        display: "flex",
        alignItems: "center",
        height: `${baseCellHeightInRem}rem`,
        cursor: "default",
    }),
    multipleValuesAndScopesIndicator: css({
        height: `${multipleValuesAndScopesIndicatorHeightInRem}rem`,
        font: text.body.regular.xSmall,
        color: themeTokens.color.text.tertiary,
        borderBottom: tableBorder,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        paddingLeft: space[16],
        paddingRight: space[16],
    }),
    overrideButtonContainer: css({
        display: "flex",
        alignItems: "center",
    }),
    parameterContainer: css({
        paddingTop: space[16],
        paddingLeft: space[4],
        paddingRight: space[4],
    }),
    summaryNode: css({
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        width: "100%",
        borderBottom: `${borderWidth[2]} solid transparent`,
        "&:hover": {
            borderBottom: `${borderWidth[2]} solid ${themeTokens.color.textField.border.hover}`,
        },
    }),
};
